// src/components/ui/navigation/Sidebar/Sidebar.jsx
import React, { useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import {
  Home,
  Calendar,
  CalendarCheck,
  Users,
  FileText,
  Workflow,
  Handshake,
  Building2,
  MessageSquare,
  Wrench,
  Code,
  LogOut,
  Star,
  Globe,
  ChevronUp,
  Check,
  Loader2,
  CreditCard
} from 'lucide-react';

// Import shadcn sidebar components
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar
} from '../../sidebar';

// Import dropdown menu
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../dropdown-menu';

// Import contexts
import { useAuth } from '../../../../contexts/AuthContext.jsx';
import { cn } from '../../../../utils/cn';

// Import UniversalHeader
import UniversalHeader from '../../layout/UniversalHeader/UniversalHeader';

// Import AI Chat Component
// import AIChat from '../../../ai/AIChat';

// FIXED: Clean SidebarMenuButton with colors on NavLink
const SidebarNavLink = ({ to, icon: Icon, label, end = false, variant = 'primary' }) => {
  // Variant styles for different navigation sections - COLORS ONLY
  const variantStyles = {
    primary: {
      active: "bg-gradient-to-r from-blue-600/20 to-blue-500/10 text-blue-300",
      inactive: "text-slate-300 hover:text-white hover:bg-slate-800/50"
    },
    config: {
      active: "bg-gradient-to-r from-emerald-600/20 to-emerald-500/10 text-emerald-300",
      inactive: "text-slate-300 hover:text-white hover:bg-slate-800/50"
    }
  };

  const currentVariant = variantStyles[variant];

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={label}>
        <NavLink
          to={to}
          end={end}
          className={({ isActive }) => 
            isActive ? currentVariant.active : currentVariant.inactive
          }
        >
          <Icon />
          <span>{label}</span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

// User Profile with Business Switcher
const SidebarUserProfile = ({ user, onLogout, allBusinesses, onSwitchBusiness, switchingBusiness }) => {
  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    const initials = names.map(n => n.charAt(0).toUpperCase()).join('');
    return initials.slice(0, 2);
  };

  const currentBusinessId = user?.business?.id;
  const hasMultipleBusinesses = allBusinesses && allBusinesses.length > 1;

  return (
    <SidebarFooter className="bg-slate-900/50">
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="bg-slate-800/50 hover:bg-slate-700/50 data-[state=open]:bg-slate-700/50"
              >
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold aspect-square size-8 items-center justify-center rounded-lg flex">
                  {getUserInitials(user?.name)}
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium text-white">
                    {user?.name || 'User'}
                  </span>
                  <span className="truncate text-xs text-slate-400">
                    {user?.business?.name || user?.email}
                  </span>
                </div>
                <ChevronUp className="ml-auto size-4 text-slate-400" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side="top"
              align="end"
              sideOffset={4}
            >
              {/* User info header */}
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </DropdownMenuLabel>

              {/* Business Switcher - only show if multiple businesses */}
              {hasMultipleBusinesses && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Switch Business
                  </DropdownMenuLabel>
                  {allBusinesses.map((business) => (
                    <DropdownMenuItem
                      key={business.id}
                      onClick={() => onSwitchBusiness(business.id)}
                      disabled={switchingBusiness}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <Building2 className="size-4" />
                          <span>{business.name}</span>
                        </div>
                        {business.id === currentBusinessId && (
                          <Check className="size-4 text-green-500" />
                        )}
                        {switchingBusiness && business.id !== currentBusinessId && (
                          <Loader2 className="size-4 animate-spin" />
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </>
              )}

              <DropdownMenuSeparator />

              {/* Logout */}
              <DropdownMenuItem onClick={onLogout} className="cursor-pointer text-red-500 focus:text-red-500">
                <LogOut className="mr-2 size-4" />
                <span>Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  );
};

// Main Sidebar Component
const SidebarContentComponent = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'owner';
  const isPlatformOwner = currentUser?.is_platform_owner === true;

  // AI Chat state
  const [isAIChatExpanded, setIsAIChatExpanded] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Navigation items configuration
  const navItems = [
    {
      to: '/dashboard',
      icon: Home,
      label: 'Dashboard',
      end: true
    },
    {
      to: '/calendar',
      icon: Calendar,
      label: 'Calendar'
    },
    {
      to: '/appointments',
      icon: CalendarCheck,
      label: 'Appointments'
    },
    {
      to: '/customers',
      icon: Users,
      label: 'Customers'
    },
    {
      to: '/invoices',
      icon: FileText,
      label: 'Invoices'
    },
    {
      to: '/reviews',
      icon: Star,
      label: 'Reviews'
    },
    {
      to: '/workflows',
      icon: Workflow,
      label: 'Automation'
    }
    // {
    //   to: '/partners',
    //   icon: Handshake,
    //   label: 'Partners'
    // }
  ];

  // Website Integration items
  const websiteItems = [
    {
      to: '/widgets',
      icon: Code,
      label: 'Widgets'
    },
    {
      to: '/site-editor',
      icon: Globe,
      label: 'Site Editor'
    }
  ];

  // Configuration items (admin only)
  const configItems = isAdmin ? [
    {
      to: '/configuration/business',
      icon: Building2,
      label: 'Business'
    },
    {
      to: '/configuration/communications',
      icon: MessageSquare,
      label: 'Communications'
    },
    {
      to: '/configuration/services',
      icon: Wrench,
      label: 'Services'
    },
    {
      to: '/configuration/team',
      icon: Users,
      label: 'Team'
    }
  ] : [];

  return (
    <ShadcnSidebar
      collapsible="icon"
      className={cn(
        "bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900",
        "border-r border-slate-700/50 backdrop-blur-xl",
        "shadow-2xl shadow-black/25"
      )}
    >
      {/* Glassmorphism overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />

      <SidebarHeader>
        <SidebarTrigger className="text-slate-400 hover:text-slate-200" />
      </SidebarHeader>

      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarNavLink
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  end={item.end}
                  variant="primary"
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Website Integration Section */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-slate-400 uppercase tracking-wide">
            Website Integration
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {websiteItems.map((item) => (
                <SidebarNavLink
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  variant="primary"
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Configuration Section - Admin Only */}
        {isAdmin && configItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-slate-400 uppercase tracking-wide">
              Configuration
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {configItems.map((item) => (
                  <SidebarNavLink
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    label={item.label}
                    variant="config"
                  />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Platform Admin Section - Platform Owner Only */}
        {isPlatformOwner && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-slate-400 uppercase tracking-wide">
              Platform Admin
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarNavLink
                  to="/admin/billing"
                  icon={CreditCard}
                  label="Billing"
                  variant="config"
                />
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* AI Chat in Footer - Grows upward when expanded */}
      {/* <AIChat
        isExpanded={isAIChatExpanded}
        onToggleExpanded={() => setIsAIChatExpanded(!isAIChatExpanded)}
      /> */}
    </ShadcnSidebar>
  );
};

// Wrapper component that includes the provider and handles children
const Sidebar = ({ children }) => {
  return (
    <SidebarProvider>
      <SidebarContentComponent />
      <SidebarInset className="flex flex-col h-screen">
        <UniversalHeader />
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default Sidebar;